import React, { useMemo } from 'react';
import { SkeletonGroup } from '@shared/components/Skeleton';
import type { BoxProps } from '@withjoy/joykit';
import { Divider } from '@withjoy/joykit';
import type { CatalogProductFilters } from '../RefinementFacet/RefinementFacet.types';
import { REFINEMENT_FACET_LIST_SKELETON_OVERRIDES, StyleFacetListContainer, styles } from './RefinementFacetList.styles';
import { RefinementFacet } from '../RefinementFacet';
import { useRefinementFacetList } from './RefinementFacetList.controller';
import { filterSupportedAttributeList } from '../../utils/filterSupportedAttributeList';
import { useRefinementsPanelProvider } from '../RefinementsPanel';
import { sortFilters } from '../../utils/sortFilters';
import type { CatalogProductListFilters } from '../../Catalog.types';
import { filterConsolidatedPriceList } from '../../utils/filterConsolidatedPriceList';

export interface RefinementFacetListProps
  extends Readonly<{
    filters: CatalogProductFilters;
    showSkeleton: boolean;
  }> {}

const ListContainer: React.FC<BoxProps> = props => {
  return (
    <StyleFacetListContainer
      __css={styles.listContainer}
      {...props}
    />
  );
};

const RefinementFacetListRoot: React.FC<RefinementFacetListProps & BoxProps> = props => {
  const { filters, children, ...rest } = props;
  const { handleOnFilterSelect } = useRefinementFacetList();
  const { checkIsFilterGroupViewExpanded, toggleGroupFullyExpand } = useRefinementsPanelProvider();

  const sortedFilters = useMemo<CatalogProductListFilters>(() => {
    const filterList = filterSupportedAttributeList(filters);
    const consolidatedFilterList = filterConsolidatedPriceList(filterList);
    return sortFilters(consolidatedFilterList);
  }, [filters]);

  return (
    <ListContainer {...rest}>
      {sortedFilters.map((filter, idx) => {
        return (
          <React.Fragment key={idx}>
            <RefinementFacet
              filter={filter}
              defaultIsOpen={sortedFilters.length === 1}
              onFilterSelect={handleOnFilterSelect}
              checkIsFilterGroupViewExpanded={checkIsFilterGroupViewExpanded}
              toggleGroupFullyExpand={toggleGroupFullyExpand}
            />
            <Divider __css={styles.divider} />
          </React.Fragment>
        );
      })}
      {children}
    </ListContainer>
  );
};
export const refinementFacetSkeletonId = 'refinement-facet-skeleton';
export const RefinementFacetList: React.FC<RefinementFacetListProps & BoxProps> = props => {
  const { children, showSkeleton } = props;
  return (
    <>
      <SkeletonGroup
        id={refinementFacetSkeletonId}
        isReady={!showSkeleton}
        placeholder={
          <ListContainer>
            <RefinementFacet.Skeleton variant="stringListMulti" />
            <RefinementFacet.Skeleton variant="colorListMulti" />
            <RefinementFacet.Skeleton variant="someOtherVariant" />
          </ListContainer>
        }
        overrides={REFINEMENT_FACET_LIST_SKELETON_OVERRIDES}
        __css={styles.filtersScrollableContainer}
      >
        <RefinementFacetListRoot {...props}>{children}</RefinementFacetListRoot>
      </SkeletonGroup>
    </>
  );
};

RefinementFacetList.displayName = 'RefinementFacetList';
